import React, { useContext, useEffect, useState } from "react";
import {
  StyledContainer,
  StyledHeader,
  StyledHeaderText,
  ReviewButton
} from "./PatientBoard.styles";
import API from "api";
import { Box, Grid, Alert } from "@mui/material";
import { Routes, Route } from "react-router-dom";
import moment from "moment";
import SideNavBar from "./microViews/SideNavBar/SideNavBar";
import Vitals from "./microViews/Vitals/Vitals";
import Profile from "./microViews/Profile/Profile";
import Details from "./microViews/Details/Details";
import Export from "./microViews/Export/Export";
import Notes from "./microViews/Notes/Notes";
import History from "./microViews/History/History";
import Notifications from "./microViews/Notifications/Notifications";
import Encounters from "./microViews/Encounters/Encounters";
import Monitoring from "./microViews/Monitoring/Monitoring";
import Options from "./microViews/Options/Options";
import { GlobalContext } from "../../context/Provider";
import Session from "helpers/session";
import Surveys from "./microViews/Surveys/Surveys";
import Devices from "./microViews/Devices/Devices";
import SmartDevices from "./microViews/Devices/SmartDevices";
import SharedDrawer from "components/shared/Drawer/Drawer";
import Messaging from "./microViews/Messaging/Messaging";
import Combined from "./microViews/Devices/Combined";
import MenuIcon from "@mui/icons-material/Menu";
import { toLastFirstName, AddEncounterEvent } from "helpers/variables";
import { COORDINATOR, ENCOUNTER } from "helpers/appConstants";
import { kore_url } from "api/kore/index";
import {clientSettings} from "../Main/Main.utils";

export default function PatientBoard() {
  const { member, memberDispatch, encounterTimer, encounterTimerDispatch, settings, settingsDispatch } = useContext(GlobalContext);
  const { config, configDispatch } = useContext(GlobalContext);
  const [isReviewed, setIsReviewed] = useState<boolean>(
    member?.current?.acknowledged?.isAcknowledged ?? false
  );
  const [reviewStatus, setReviewStatus] = useState<
    "idle" | "loading" | "success" | "error"
  >("idle");
  let devicePath = <></>;
  const memberId = window.location.pathname.split("/").pop();

  const handleReview = () => {
    if (!member) return;
    setReviewStatus("loading");

    API.kore.members.patch(
      member.current?.id, 
      {
        acknowledged: {
          isAcknowledged: true,
          lastAcknowledged: new Date().toISOString()
        }
      }, 
      Session.crackCookieToken("coordinator"))
        .then((updatedMember) => {
          memberDispatch({
            type: "UPDATE_ACKNOWLEDGED",
            payload: updatedMember
          });
          setIsReviewed(true);
          setReviewStatus("success");
        })
        .catch(() => {
          setReviewStatus("error");
        });
  };
  const userSettings = (userId: string) => ({
    entity: "User",
    id: userId,
    system: {
      settings: {
        historyOptions: {
          activity: true,
          bloodGlucose: true,
          bloodPressure: true,
          heartRate: true,
          memberNotes: false,
          oxygenSaturation: false,
          respirationRate: false,
          spirometry: true,
          temperature: true,
          weight: true
        },
        vitalTileOptions: {
          activity: true,
          bloodGlucose: true,
          bloodPressure: true,
          heartRate: true,
          memberNotes: false,
          oxygenSaturation: false,
          respirationRate: false,
          spirometry: true,
          temperature: true,
          weight: true
        },
        chartOptions: {
          activity: true,
          bloodGlucose: true,
          bloodPressure: true,
          heartRate: true,
          memberNotes: false,
          oxygenSaturation: false,
          respirationRate: false,
          spirometry: true,
          temperature: true,
          weight: true
        },
        spirometryVisibility: {
          fev1: true,
          fvc: true,
          fev1fvc: true,
          pef: true
        }
      }
    },
    organization: "l365"
  });

  useEffect(() => {
    if (!memberId) return;
    if (!member?.current.email && memberId) {
      API.kore.members
        .getOne(memberId || "", Session.crackCookieToken("coordinator"))
        .then((member) => {
          memberDispatch({ type: "CHANGE_TARGET_MEMBER", payload: member });

          const raw = window.sessionStorage.getItem("encounter");
          if (raw) {
            try {
              const blob = JSON.parse(Session.decrypt(raw));
              if (!blob.memberId) {
                blob.memberId = member.id;
                blob.memberName = member.username;
                window.sessionStorage.setItem("encounter", Session.encrypt(JSON.stringify(blob)));
              }

              const alreadyViewed = blob.actions?.some(
                (a: any) => a.noteContent === "Patient viewed"
              );
              if (!alreadyViewed) {
                const encounterEvent = AddEncounterEvent("intervention", "Patient viewed");
                Session.updateSessionEncounter(
                  window.sessionStorage.getItem("encounter") || "",
                  encounterEvent,
                  "1"
                );
              }
            } catch (err) {
              console.error("Failed to repair encounter blob:", err);
            }
          }
        });
      API.kore.settings
        .getSingle("?entity=user", Session.crackCookieToken("coordinator"), memberId)
        .then((response: any) => {
          settingsDispatch({
            type: "CURRENT_MEMBER_SETTINGS",
            payload: {
              memberId,
              settings: response.data.system.settings
            }
          });
        })
        .catch((err: any) => {
          if (err?.response?.status === 404) {
            return API.kore.settings.create(
              userSettings(memberId),
              Session.crackCookieToken("coordinator")
            )
              .then((response: any) => {
                settingsDispatch({
                  type: "CURRENT_MEMBER_SETTINGS",
                  payload: {
                    memberId,
                    settings: response.data.system.settings
                  }
                });
              });
          } else {
            console.log("Error loading user settings");
          }
        });
    }
    API.kore.configurations
      .getConfigurations()
      .then((configs) => {
        configDispatch({ type: "OPTIONS_UPLOAD", payload: configs });
      })
      .catch((err) => console.log(err));

    setIsReviewed(member?.current?.acknowledged?.isAcknowledged ?? false);
  }, [memberId]);

  useEffect(() => {
    encounterTimerDispatch({
      type: "START_ENCOUNTER",
      payload: {
        current: {
          memberId,
          actions: [],
          duration: 0,
          escalatedDuration: 0,
          pause: false
        }
      }
    });

    return () => {
      encounterTimerDispatch({ type: "RESET_ENCOUNTER_TIMER" });
    };
  }, [memberId]);

  useEffect(() => {
    const timer = encounterTimer?.current;
    if(!timer) return;
    if (timer.pause) return;
    const interval = setInterval(() => {
      timer.duration += 1;
    }, 1000);
    return () => clearInterval(interval);
  }, [encounterTimer?.current?.pause]);

  useEffect(() => {
    if (reviewStatus === "success" || reviewStatus === "error") {
      const timer = setTimeout(() => {
        setReviewStatus("idle");
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [reviewStatus]);

  function deviceRoute() {
    if (
      config?.basicOptions.cellDevices === true &&
      config?.basicOptions.smartDevices === true
    ) {
      return (devicePath = <Combined />);
    } else if (config?.basicOptions.cellDevices === true) {
      return (devicePath = <Devices memberId={memberId} />);
    } else {
      return (devicePath = <SmartDevices />);
    }
  }
  const patientBoardIcon = <MenuIcon />;
  const patientBoardMenuItem = (
    <Box sx={{ mb: 2 }}>
      <SideNavBar></SideNavBar>
    </Box>
  );

  deviceRoute();
  return (
    <Grid container>
      <Grid item xs={1} />
      <Grid item xs={10}>
        <StyledContainer>
          <StyledHeader>
            <StyledHeaderText>{`${toLastFirstName(
              `${member?.current.firstName} ${member?.current.lastName}`
            )} ${
              parseInt(moment().format("YYYY")) -
                parseInt(moment(member?.current.birthDate).format("YYYY")) || ""
            }`}</StyledHeaderText>
            <Box display="flex" alignItems="center" gap={2} paddingBottom={.7}>
              {reviewStatus === "success" && (
                <Alert severity="success" sx={{ py: 0 }}>
                  Review Saved
                </Alert>
              )}
              {reviewStatus === "error" && (
                <Alert severity="error" sx={{ py: 0 }}>
                  Failed to Save
                </Alert>
              )}
              <ReviewButton
                disabled={isReviewed || reviewStatus === "loading"}
                onClick={handleReview}
              >
                {reviewStatus === "loading"
                  ? "Saving..."
                  : isReviewed
                  ? "Reviewed"
                  : "Mark as Reviewed"
                }
              </ReviewButton>
            </Box>
          </StyledHeader>
          <Grid container>
            <Grid item xs={0} md={2}>
              <Box sx={{ display: { xs: "none", md: "block" } }}>
                <SideNavBar />
              </Box>
            </Grid>
            <Grid item xs={10}>
              <Box sx={{ display: { xs: "block", md: "none" } }}>
                <SharedDrawer
                  icon={patientBoardIcon}
                  menuItems={patientBoardMenuItem}
                  origin={"patientBoard"}
                />
              </Box>
              <Routes>
                <Route
                  path="profile/:memberId"
                  element={
                    <Profile
                      type="members"
                      action="edit"
                      isAvatar={false}
                      memberId={memberId ?? ""}
                    />
                  }
                />
                <Route
                  path="details/:memberId"
                  element={<Details
                      type="member"
                      action="edit"
                      memberId={memberId ?? ""}
                  />}
                />
                <Route path="notes/:memberId" element={<Notes memberId={memberId ?? ""} />} />
                <Route path="vitals/:memberId" element={<Vitals/>} />
                <Route path="history/*" element={<History/>} />
                <Route path="surveys/:memberId" element={<Surveys memberId={memberId ?? ""}/>} />
                <Route path="export/:memberId" element={<Export />} />
                <Route
                  path="notifications/:memberId"
                  element={<Notifications memberId={memberId ?? ""}/>}
                />
                <Route path="encounters/:memberId" element={<Encounters/>} />
                <Route path="monitoring/:memberId" element={<Monitoring memberId={memberId ?? ""}/>} />
                <Route path="messaging/:memberId" element={<Messaging />} />
                <Route path="devices/:memberId" element={devicePath} />
                <Route path="options/:memberId" element={<Options memberId={memberId ?? ""}/>} />
              </Routes>
            </Grid>
          </Grid>
        </StyledContainer>
      </Grid>
      <Grid item xs={1} />
    </Grid>
  );
}
